import { memo, useEffect } from "react";
import { useSessionStore } from "../store/useSessionStore";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { BodyCopy } from "../components/BodyCopy";
import { Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { usePartnerLogoSrc } from "../hooks/usePartnerLogoSrc";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { getEvents, safeCapture } from "../analytics";
import { useRequestStore } from "../store/useRequestStore";
import { requesterEntryRoute } from "../utils/requesterScreening";

const SPLASH_DURATION_MS = 2000;

/**
 * Entry screen. Claims the magic link, shows a partner-logo splash for 2s only
 * when splash is enabled and a logo is available, then routes to the first
 * flow screen (or an error screen).
 */
export const LoadingPage = memo(() => {
  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("loading"));
  }, []);

  useClaimLink({ revalidate: true });

  const { redirectTo } = useRouteWithToken();
  const { session } = useSessionStore();
  const hasDraft = useRequestStore((state) => state.draft != null);
  const { theme } = useStore();
  const colorConfig = theme.whiteView;

  const urlLinkId = resolveUrlLinkId();
  const sessionMatchesUrl =
    session.status === RequestStatus.LOADED &&
    Boolean(session.data?.linkId) &&
    (!urlLinkId || session.data?.linkId === urlLinkId);
  const linkId = sessionMatchesUrl ? session.data?.linkId : undefined;
  const { src: logoSrc, settled: logoSettled } = usePartnerLogoSrc(linkId);

  // The splash is opt-out: it shows by default and is skipped only when the
  // link was created with `config.showSplash === false` (e.g. the flow is
  // embedded inside another flow). The partner theme is applied regardless
  // (useReadTheme runs off the claimed session) so skipping the splash causes
  // no visual discontinuity.
  const splashEnabled = session.data?.config?.showSplash !== false;

  // Welcome is mandatory until the user has accepted the current terms version.
  const firstScreen = requesterEntryRoute(session.data?.config, {
    termsAccepted: Boolean(session.data?.termsAccepted),
    hasDraft,
  });

  const showLogoSplash =
    splashEnabled && sessionMatchesUrl && Boolean(logoSrc);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;

    if (sessionMatchesUrl) {
      if (!splashEnabled) {
        // Splash skipped: route as soon as the session (and theme) are ready.
        redirectTo(firstScreen);
      } else if (logoSettled) {
        if (logoSrc) {
          timer = setTimeout(() => {
            redirectTo(firstScreen);
          }, SPLASH_DURATION_MS);
        } else {
          redirectTo(firstScreen);
        }
      }
    } else if (session.status === RequestStatus.ERROR && session.error) {
      // Claim failures: skip splash delay and show ErrorPage immediately.
      // ErrorPage hides the CTA when btnText is null (link expired / not found).
      const recoverable =
        session.error.errorCode === LINK_ERROR_CODE.LINK_EXPIRED ||
        session.error.errorCode === LINK_ERROR_CODE.LINK_REVOKED;
      const maintenance =
        session.error.errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE;
      redirectTo(
        maintenance
          ? APP_ROUTES.MAINTENANCE_PAGE
          : recoverable
            ? APP_ROUTES.SESSION_EXPIRED_PAGE
            : APP_ROUTES.ERROR_PAGE,
        {
          replace: true,
          state: session.error,
        },
      );
    }

    return () => clearTimeout(timer);
  }, [
    sessionMatchesUrl,
    session.status,
    session.error,
    redirectTo,
    logoSettled,
    logoSrc,
    splashEnabled,
    firstScreen,
  ]);

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Header>
        <BodyCopy className="footer-text">
          <label className="muted-foreground" style={{ color: "#787878" }}>
            <Trans i18nKey="loader.sponsored" />
          </label>
        </BodyCopy>
      </Layout.Header>

      <Layout.Content>
        <div
          className={`landing-section content-center loader-page ${
            showLogoSplash ? "splash-screen" : ""
          }`}
        >
          {showLogoSplash ? (
            <img
              src={logoSrc!}
              alt={session.data?.partner?.name ?? "Partner logo"}
              className="partner-logo-splash"
            />
          ) : (
            <section className="content">
              <div
                className="loader-spinner"
                role="status"
                aria-label="Loading"
              />
            </section>
          )}
        </div>
      </Layout.Content>

      <Layout.Footer>
        <label className="muted-foreground patriot-text" style={{ color: "#787878" }}>
          {(() => {
            const patriotAct = t("loader.patriotAct");
            const colon = patriotAct.indexOf(":");
            if (colon < 0) return patriotAct;
            return (
              <>
                <strong>{patriotAct.slice(0, colon + 1)}</strong>
                {patriotAct.slice(colon + 1)}
              </>
            );
          })()}
        </label>
      </Layout.Footer>
    </AuraLayout>
  );
});
